// ── Navigation config ────────────────────────────────────────────────────────
// Single source of truth for the sidebar sections and their in-page tabs.
// Merges the old icon-rail + FlowNav lifecycle stages into one labelled sidebar
// (SOURCE/amplehire-frontend-v2 structure). Clicking a section opens its default
// route; sub-options render as tabs (see SubNav) and reuse the SAME components.
import {
  LayoutDashboard, Briefcase, Users, Video, GitBranch,
  Sparkles, BarChart3, Settings, UserCog,
} from "lucide-react";

export const SECTIONS = [
  {
    key: "dashboard",
    label: "Dashboard",
    desc: "Real-time overview",
    color: "#7B5AC4",
    Icon: LayoutDashboard,
    to: "/admin/dashboard-v2",
    tourId: "nav-home",
    tabs: [],
  },
  {
    key: "jobs",
    label: "Jobs",
    desc: "Plan & define roles",
    color: "#2E7DAF",
    Icon: Briefcase,
    to: "/admin/jobs",
    tabs: [
      { label: "Jobs", to: "/admin/jobs" },
      { label: "Questions", to: "/admin/question-bank" },
      { label: "Screening", to: "/admin/define-interview" },
    ],
  },
  {
    key: "candidates",
    label: "Candidates",
    desc: "Source & track talent",
    color: "#1F8A7D",
    Icon: Users,
    to: "/admin/talent-pools",
    tabs: [
      { label: "Talent pools", to: "/admin/talent-pools" },
      { label: "Resumes", to: "/admin/upload-resumes" },
      { label: "Source hub", to: "/admin/source-hub" },
      { label: "Invite", to: "/admin/invite-candidates" },
      { label: "Track", to: "/admin/invited-candidates" },
    ],
  },
  {
    key: "interviews",
    label: "Interviews",
    desc: "Schedule & review",
    color: "#B865A8",
    Icon: Video,
    to: "/admin/interviews",
    tabs: [
      { label: "Sessions", to: "/admin/interviews" },
      { label: "Report", to: "/admin/candidate-report" },
      { label: "Live", to: "/admin/live" },
    ],
  },
  {
    key: "pipeline",
    label: "Pipeline",
    desc: "Compare, hire & offer",
    color: "#16A34A",
    Icon: GitBranch,
    to: "/admin/pipeline",
    tabs: [
      { label: "Pipeline", to: "/admin/pipeline" },
      { label: "Shortlisted", to: "/admin/shortlisted" },
      { label: "Offered", to: "/admin/offered" },
      { label: "Hired", to: "/admin/hired" },
      { label: "Compare", to: "/admin/compare" },
    ],
  },
  {
    key: "ai",
    label: "AI",
    desc: "Ask the assistant",
    color: "#9B4F9B",
    Icon: Sparkles,
    to: "/admin/assistant",
    tourId: "ai-assistant",
    tabs: [],
  },
  {
    key: "reports",
    label: "Reports",
    desc: "Analytics & insights",
    color: "#B8942F",
    Icon: BarChart3,
    to: "/admin/reports",
    adminOnly: true,
    tabs: [],
  },
  {
    key: "team",
    label: "Team",
    desc: "Members & roles",
    color: "#C2568C",
    Icon: UserCog,
    to: "/admin/user-management",
    adminOnly: true,
    tabs: [
      { label: "Members", to: "/admin/user-management" },
      { label: "Roles", to: "/admin/role" },
    ],
  },
  {
    key: "settings",
    label: "Settings",
    desc: "Configure workspace",
    color: "#64748B",
    Icon: Settings,
    to: "/admin/settings",
    adminOnly: true,
    tabs: [
      { label: "Setup", to: "/admin/settings" },
      { label: "Integrations", to: "/admin/integrations" },
      { label: "Scheduling", to: "/admin/scheduling" },
      { label: "Communications", to: "/admin/communications" },
      { label: "Email templates", to: "/admin/email-templates" },
      { label: "Search candidates", to: "/admin/search-candidates" },
      { label: "Search interviews", to: "/admin/search-interviews" },
      { label: "Audit", to: "/admin/audit" },
      { label: "Exports", to: "/admin/exports" },
    ],
  },
];

// Every route owned by a section (default + tabs) — used for active matching.
const sectionRoutes = (s) => [s.to, ...s.tabs.map((t) => t.to)];

// Resolve the active section for a path, preferring the longest matching prefix.
export function findSection(pathname) {
  let best = null;
  let bestLen = -1;
  for (const s of SECTIONS) {
    for (const r of sectionRoutes(s)) {
      if ((pathname === r || pathname.startsWith(r + "/")) && r.length > bestLen) {
        best = s;
        bestLen = r.length;
      }
    }
  }
  return best;
}

// Resolve the active tab within a section (longest matching prefix).
export function findActiveTab(section, pathname) {
  if (!section) return null;
  let best = null;
  let bestLen = -1;
  for (const t of section.tabs) {
    if ((pathname === t.to || pathname.startsWith(t.to + "/")) && t.to.length > bestLen) {
      best = t;
      bestLen = t.to.length;
    }
  }
  return best;
}
